// Core
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

// Components
import { Button, Chip, FieldSelect, FieldText, Modal, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import {
  openNotificationPopup,
  openSuccessAlert,
  openWarningAlert,
} from "state/application/applicationSlice";
import { useGetCommunitiesByUserQuery } from "state/communities/communitiesApiSlice";
import {
  useCreateIntegrationMutation,
  useDeleteIntegrationMutation,
  useGetIntegrationsQuery,
  useTestIntegrationMutation,
  useUpdateIntegrationMutation,
} from "state/integrations/integrationsApiSlice";
import IntegrationWebhooksModal from "./IntegrationWebhooksModal";

/* Provider catalogue: label shown in the UI and the default API base URL.
   GDBN runs two generations of its backend with different APIs. */
const PROVIDERS = [
  { value: "GDBN", label: "GDBN (v2)", baseUrl: "https://gdbn.eu/backendv2" },
  { value: "GDBN_V1", label: "GDBN (v1)", baseUrl: "https://gdbn.eu/backend" },
];
const providerLabel = (value) => PROVIDERS.find((p) => p.value === value)?.label || value;
const providerBaseUrl = (value) => PROVIDERS.find((p) => p.value === value)?.baseUrl || "";

const STATUS_TONE = { connected: "teal", error: "red", pending: "gray" };

const blankForm = () => ({
  provider: "GDBN",
  base_url: providerBaseUrl("GDBN"),
  email: "",
  password: "",
  community_id: "",
});

function IntegrationsSection() {
  const { t } = useTranslation(["settings"]);
  const dispatch = useDispatch();

  const { data: integrations = [], isLoading } = useGetIntegrationsQuery();
  const { data: communitiesData } = useGetCommunitiesByUserQuery();

  const [createIntegration, { isLoading: isCreating }] = useCreateIntegrationMutation();
  const [updateIntegration, { isLoading: isUpdating }] = useUpdateIntegrationMutation();
  const [deleteIntegration] = useDeleteIntegrationMutation();
  const [testIntegration, { isLoading: isTesting }] = useTestIntegrationMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // integration being edited, or null
  const [form, setForm] = useState(blankForm());
  const [webhooksFor, setWebhooksFor] = useState(null); // integration for webhook modal
  // login test rejected on save; nothing was persisted (shown inline on the password field)
  const [credentialsError, setCredentialsError] = useState(false);

  const communityOptions = useMemo(() => {
    const none = { value: "", label: t("integrations.community_none") };
    if (!communitiesData?.ids) return [none];
    return [
      none,
      ...communitiesData.ids.map((id) => ({
        value: id,
        label: communitiesData.entities[id]?.name || id,
      })),
    ];
  }, [communitiesData, t]);

  const communityLabel = (id) => (id && communitiesData?.entities?.[id]?.name) || null;

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setCredentialsError(false);
    setModalOpen(true);
  };

  const openEdit = (integration) => {
    setEditing(integration);
    setForm({
      provider: integration.provider,
      base_url: integration.base_url,
      email: integration.email,
      password: "",
      community_id: integration.community_id || "",
    });
    setCredentialsError(false);
    setModalOpen(true);
  };

  const setField = (key) => (v) => {
    setCredentialsError(false);
    setForm((f) => {
      if (key === "provider") {
        // switching provider resets the URL to that backend's default
        return { ...f, provider: v, base_url: providerBaseUrl(v) };
      }
      return { ...f, [key]: v };
    });
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const body = {
          provider: form.provider,
          base_url: form.base_url,
          email: form.email,
          community_id: form.community_id || null,
        };
        if (form.password) body.password = form.password;
        await updateIntegration({ integrationId: editing.id, body }).unwrap();
        dispatch(openSuccessAlert({ title: t("integrations.updated_success") }));
      } else {
        await createIntegration({
          provider: form.provider,
          base_url: form.base_url,
          email: form.email,
          password: form.password,
          community_id: form.community_id || null,
        }).unwrap();
        dispatch(openSuccessAlert({ title: t("integrations.created_success") }));
      }
      setModalOpen(false);
    } catch (error) {
      if (error?.status === 401) {
        // credential test failed on the backend — the changes were NOT saved
        setCredentialsError(true);
        return;
      }
      dispatch(
        openNotificationPopup({
          notification: error?.data?.detail || t("integrations.error_generic"),
          type: "error",
        })
      );
    }
  };

  const handleDelete = (integration) => {
    dispatch(
      openWarningAlert({
        title: t("integrations.delete_confirm_title"),
        text: t("integrations.delete_confirm_text"),
        confirmText: t("integrations.delete_confirm_button"),
        isDangerous: true,
        icon: "warning",
        confirmAction: async () => {
          try {
            await deleteIntegration(integration.id).unwrap();
            dispatch(openSuccessAlert({ title: t("integrations.deleted_success") }));
          } catch (error) {
            dispatch(
              openNotificationPopup({
                notification: error?.data?.detail || t("integrations.error_generic"),
                type: "error",
              })
            );
          }
        },
      })
    );
  };

  const handleTest = async (integration) => {
    try {
      const result = await testIntegration(integration.id).unwrap();
      if (result.status !== "connected") {
        throw new Error(result.status_detail || "");
      }
      dispatch(openSuccessAlert({ title: t("integrations.status_connected") }));
    } catch (error) {
      dispatch(
        openNotificationPopup({
          notification: error?.data?.detail || error?.message || t("integrations.error_generic"),
          type: "error",
        })
      );
    }
  };

  const busy = isCreating || isUpdating;
  const canSave = form.base_url && form.email && (editing ? true : form.password.length > 0);

  return (
    <SectionCard
      id="integrations"
      icon="hub"
      title={t("integrations.title")}
      sub={t("integrations.sub")}
      action={
        <Button variant="ghost" size="sm" onClick={openCreate}>
          <Icon name="add" />
          {t("integrations.add")}
        </Button>
      }
    >
      {!isLoading && integrations.length === 0 && (
        <p className="text-[13px] text-ink-3 m-0">{t("integrations.empty")}</p>
      )}

      <div className="flex flex-col gap-3">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="flex items-center gap-3 flex-wrap"
            style={{
              padding: 14,
              background: "var(--surface-2)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-[10px] flex-wrap">
                <span className="text-[13.5px] font-semibold text-ink">
                  {providerLabel(integration.provider)}
                </span>
                <Chip tone={STATUS_TONE[integration.status] || "gray"}>
                  {t(`integrations.status_${integration.status}`)}
                </Chip>
              </div>
              <div className="text-[12px] text-ink-3 mt-[2px] truncate">
                {integration.email} · {integration.base_url}
                {communityLabel(integration.community_id) &&
                  ` · ${communityLabel(integration.community_id)}`}
              </div>
            </div>
            <div className="flex items-center gap-[6px] flex-wrap">
              <Button variant="ghost" size="sm" onClick={() => setWebhooksFor(integration)}>
                <Icon name="webhook" />
                {t("integrations.webhooks.manage")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={isTesting}
                onClick={() => handleTest(integration)}
              >
                <Icon name="sync" />
                {t("integrations.test")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => openEdit(integration)}>
                <Icon name="edit" />
                {t("integrations.edit")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="!text-danger"
                onClick={() => handleDelete(integration)}
              >
                <Icon name="delete" />
                {t("integrations.delete")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t("integrations.edit") : t("integrations.add")}
        subtitle={providerLabel(form.provider)}
        maxWidth={560}
        footer={
          <div className="flex items-center justify-end gap-[10px]">
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={busy}>
              {t("integrations.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={busy || !canSave}>
              <Icon name="link" />
              {editing ? t("integrations.save") : t("integrations.connect")}
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-[14px]">
          <FieldSelect
            label={t("integrations.provider")}
            value={form.provider}
            options={PROVIDERS}
            onChange={setField("provider")}
          />
          <FieldText
            label={t("integrations.base_url")}
            value={form.base_url}
            onChange={setField("base_url")}
          />
          <FieldText
            label={t("integrations.email")}
            type="email"
            value={form.email}
            onChange={setField("email")}
          />
          <FieldText
            label={t("integrations.password")}
            type="password"
            reveal
            value={form.password}
            error={credentialsError}
            hint={
              credentialsError ? (
                <span style={{ color: "var(--danger)" }}>
                  {t("integrations.invalid_credentials")}
                </span>
              ) : (
                (editing && t("integrations.password_edit_hint")) || null
              )
            }
            onChange={setField("password")}
          />
          <FieldSelect
            label={t("integrations.community")}
            value={form.community_id}
            options={communityOptions}
            onChange={setField("community_id")}
          />
        </div>
      </Modal>

      {webhooksFor && (
        <IntegrationWebhooksModal integration={webhooksFor} onClose={() => setWebhooksFor(null)} />
      )}
    </SectionCard>
  );
}

export default IntegrationsSection;
